import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { History, RotateCcw, User } from 'lucide-react';
import type { CreatePostInput } from '@news-cms/shared';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { htmlFromBlocks } from '@/lib/editor-blocks';
import { absolutizeMediaUrls } from '@/lib/media';
import { postRevisionsQueryOptions, revisionDetailQueryOptions, useUpdatePost } from '@/lib/posts';

interface RevisionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  postId: string;
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('th-TH', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function RevisionsDialog({ open, onOpenChange, postId }: RevisionsDialogProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const listQuery = useQuery({
    ...postRevisionsQueryOptions(postId),
    enabled: open,
  });

  const detailQuery = useQuery({
    ...revisionDetailQueryOptions(postId, selectedId ?? ''),
    enabled: open && selectedId !== null,
  });

  const update = useUpdatePost(postId);

  const handleRestore = async () => {
    const snap = detailQuery.data?.snapshot;
    if (!snap) return;
    const payload: Partial<CreatePostInput> = {
      title: snap.title,
      summary: snap.summary ?? null,
      body: htmlFromBlocks(snap.body),
      metaTitle: snap.meta?.metaTitle ?? null,
      metaDescription: snap.meta?.metaDescription ?? null,
      focusKeyword: snap.meta?.focusKeyword ?? null,
    };
    await update.mutateAsync(payload);
    setConfirmOpen(false);
    onOpenChange(false);
    setSelectedId(null);
  };

  const revisions = listQuery.data ?? [];
  const selectedDetail = detailQuery.data;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[80vh] max-w-3xl overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              ประวัติการแก้ไข
            </DialogTitle>
            <DialogDescription>
              เลือก revision เพื่อดูเนื้อหา หรือกู้คืนกลับมาเป็นเวอร์ชันปัจจุบัน (เก็บ 20
              รายการล่าสุด)
            </DialogDescription>
          </DialogHeader>

          <div className="grid max-h-[60vh] grid-cols-1 gap-4 overflow-auto md:grid-cols-2">
            {/* List */}
            <div className="space-y-1 md:border-r md:border-slate-200 md:pr-3 md:dark:border-slate-800">
              {listQuery.isLoading && <p className="text-sm text-slate-500">กำลังโหลด...</p>}
              {!listQuery.isLoading && revisions.length === 0 && (
                <p className="text-sm text-slate-500">ยังไม่มี revision</p>
              )}
              {revisions.map((r) => {
                const selected = r.id === selectedId;
                return (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => setSelectedId(r.id)}
                    className={
                      selected
                        ? 'bg-brand-50 dark:bg-brand-900/30 block w-full rounded-md px-3 py-2 text-left text-xs'
                        : 'block w-full rounded-md px-3 py-2 text-left text-xs hover:bg-slate-50 dark:hover:bg-slate-800/50'
                    }
                  >
                    <div className="font-medium text-slate-900 dark:text-slate-50">
                      {formatDateTime(r.createdAt)}
                    </div>
                    <div className="mt-0.5 flex items-center gap-1 text-slate-500">
                      <User className="h-3 w-3" />
                      {r.authorName}
                    </div>
                    {r.note && <div className="mt-0.5 italic text-slate-400">{r.note}</div>}
                  </button>
                );
              })}
            </div>

            {/* Preview */}
            <div>
              {selectedId === null && (
                <p className="py-8 text-center text-sm text-slate-400">
                  เลือก revision เพื่อดูเนื้อหา
                </p>
              )}
              {selectedId !== null && detailQuery.isLoading && (
                <p className="text-sm text-slate-500">กำลังโหลด snapshot...</p>
              )}
              {selectedDetail && (
                <div className="space-y-3 text-sm">
                  <div>
                    <div className="text-[11px] uppercase text-slate-400">หัวข้อ</div>
                    <div className="font-medium text-slate-900 dark:text-slate-50">
                      {selectedDetail.snapshot.title}
                    </div>
                  </div>
                  {selectedDetail.snapshot.summary && (
                    <div>
                      <div className="text-[11px] uppercase text-slate-400">โปรยข่าว</div>
                      <p className="text-slate-700 dark:text-slate-300">
                        {selectedDetail.snapshot.summary}
                      </p>
                    </div>
                  )}
                  <div>
                    <div className="mb-1 text-[11px] uppercase text-slate-400">เนื้อหา</div>
                    <div
                      className="tiptap-content max-h-64 overflow-y-auto rounded-md border border-slate-200 bg-slate-50 p-3 text-xs dark:border-slate-800 dark:bg-slate-900"
                      dangerouslySetInnerHTML={{
                        __html: absolutizeMediaUrls(htmlFromBlocks(selectedDetail.snapshot.body)),
                      }}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="default"
                    size="sm"
                    className="w-full"
                    onClick={() => setConfirmOpen(true)}
                    disabled={update.isPending}
                  >
                    <RotateCcw className="mr-2 h-4 w-4" />
                    กู้คืน revision นี้
                  </Button>
                  <p className="text-[11px] text-slate-500">
                    การกู้คืนจะแทนหัวข้อ / โปรยข่าว / เนื้อหา / meta ด้วย snapshot นี้ —
                    สถานะการเผยแพร่ + flags ยังคงเหมือนเดิม
                  </p>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="ยืนยันการกู้คืน"
        description="เนื้อหาปัจจุบันจะถูกแทนด้วย revision นี้ (snapshot จะถูกบันทึกไว้เพื่อกู้คืนกลับได้)"
        confirmLabel="กู้คืน"
        variant="default"
        isLoading={update.isPending}
        onConfirm={() => {
          void handleRestore();
        }}
      />
    </>
  );
}
